import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {QueryKey} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useComparisonInfo} from '../../stores/selectors'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import type {RichDiff} from '@github-ui/diff-lines/types'

interface QueryKeyFields {
  basePath: string
  selectedRange: {
    baseOid: string
    headOid: string
  }
  path: string
}

export type RichDiffResponse = Pick<RichDiff, 'proseDiffHtml' | 'renderInfo' | 'dependencyDiffPath'>

type FetchRichDiffArgs = {
  path: string
  signal?: AbortSignal
  basePath: string
  fullDiff: {
    baseOid: string
    headOid: string
  }
} & QueryKeyFields

export function richDiffKey({basePath, selectedRange, path}: QueryKeyFields): QueryKey {
  return [
    PageData.richDiff,
    basePath,
    `baseOid:${selectedRange.baseOid}`,
    `headOid:${selectedRange.headOid}`,
    `path:${path}`,
  ]
}

export function useRichDiffQueryConfig({basePath, path}: {basePath: string; path: string}) {
  const comparisonInfo = useComparisonInfo()
  const queryKey = richDiffKey({basePath, path, ...comparisonInfo})

  const queryArgs = {
    path,
    basePath,
    ...comparisonInfo,
    signal: undefined, // No abort signal for now
  }

  const queryConfig = {
    fetcher: fetchRichDiff,
    key: queryKey,
    args: queryArgs,
  }

  return queryConfig
}

export async function fetchRichDiff({
  basePath,
  fullDiff,
  selectedRange,
  path,
  signal,
}: FetchRichDiffArgs): Promise<RichDiffResponse | undefined> {
  const params = new URLSearchParams()
  params.append('path', path)

  if (selectedRange.baseOid === fullDiff.baseOid) {
    params.append('range', selectedRange.headOid)
  } else {
    params.append('range', `${selectedRange.baseOid}..${selectedRange.headOid}`)
  }

  const fetchUrl = `${basePath}/page_data/${PageData.richDiff}?${params.toString()}`

  try {
    const response = await reactFetchJSON(fetchUrl, {signal})
    if (signal?.aborted) return undefined
    const data = await parseJSONWithBetterErrors(response)
    throwErrorsIfBadResponse(response, data)
    return data ?? undefined
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return
    } else {
      throw error
    }
  }
}
